// Pure helpers for the admin Case Management page (bulk close / reassign of
// stale cases). Nothing here touches React or the network directly — the
// chunked runners take the POST function as an argument — so it is all
// unit-testable (caseManagementUtils.test.ts).
import {
  BulkCloseResult,
  BulkReassignResult,
  ManagedCaseRow,
  ManagedCasesResponse,
  UserType,
} from "../../../interfaces";

export type NeverMode = "include" | "exclude" | "only";
export type ActivityBasis = "any" | "interaction";
export type SecondaryMode = "any" | "none_active";
export type SortOrder = "desc" | "asc";

// The filter half of GET /cases/manage (and /cases/manage/ids). It is also
// the page's URL state, so a filtered view is bookmarkable.
export interface CaseManageFilters {
  /** User ObjectId hex strings and/or the `inactive` / `missing` sentinels. */
  assigned: string[];
  inactiveDays: number;
  never: NeverMode;
  basis: ActivityBasis;
  secondary: SecondaryMode;
  /** Firm-name substring (already trimmed). */
  q: string;
  order: SortOrder;
}

export const ASSIGNED_INACTIVE = "inactive";
export const ASSIGNED_MISSING = "missing";

export const DEFAULT_INACTIVE_DAYS = 90;
export const MIN_INACTIVE_DAYS = 1;
export const MAX_INACTIVE_DAYS = 3650;
export const INACTIVE_DAY_PRESETS = [90, 180, 365];

/** Server cap per bulk POST (close and reassign). */
export const BULK_CHUNK_SIZE = 500;
/** Cases opened fewer than this many days ago are never bulk-closed. */
export const YOUNG_CASE_DAYS = 30;
/** Rows per request when the page pulls a whole filter result (CSV export). */
export const EXPORT_PAGE_SIZE = 200;
/** Same cap as GET /cases/manage/ids. */
export const EXPORT_ROW_CAP = 5000;

export const REASON_MIN = 3;
export const REASON_MAX = 500;

export const DEFAULT_FILTERS: CaseManageFilters = {
  assigned: [],
  inactiveDays: DEFAULT_INACTIVE_DAYS,
  never: "include",
  basis: "any",
  secondary: "any",
  q: "",
  order: "desc",
};

const OBJECT_ID_RE = /^[0-9a-f]{24}$/i;

/** Clamp a free-typed day count to the server's 1..3650; null when not a number. */
export const clampInactiveDays = (raw: unknown): number | null => {
  if (raw === null || raw === undefined || raw === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.min(MAX_INACTIVE_DAYS, Math.max(MIN_INACTIVE_DAYS, Math.floor(n)));
};

/**
 * `assigned` items the server understands: 24-hex user ids and the two
 * sentinels. Anything else off a hand-edited URL is dropped, and duplicates
 * collapse, so the query never carries junk.
 */
export const sanitizeAssigned = (items: string[]): string[] => {
  const out: string[] = [];
  items.forEach((raw) => {
    const item = String(raw || "").trim();
    if (!item) return;
    const ok =
      item === ASSIGNED_INACTIVE ||
      item === ASSIGNED_MISSING ||
      OBJECT_ID_RE.test(item);
    if (ok && !out.includes(item)) out.push(item);
  });
  return out;
};

const oneOf = <T extends string>(
  raw: string | null,
  allowed: readonly T[],
  fallback: T,
): T => (raw && (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback);

/** URL → filters. Unknown / invalid values fall back to the defaults. */
export const filtersFromParams = (params: URLSearchParams): CaseManageFilters => ({
  assigned: sanitizeAssigned((params.get("assigned") || "").split(",")),
  inactiveDays:
    clampInactiveDays(params.get("inactiveDays")) ?? DEFAULT_INACTIVE_DAYS,
  never: oneOf(params.get("never"), ["include", "exclude", "only"] as const, "include"),
  basis: oneOf(params.get("basis"), ["any", "interaction"] as const, "any"),
  secondary: oneOf(params.get("secondary"), ["any", "none_active"] as const, "any"),
  q: (params.get("q") || "").trim(),
  order: oneOf(params.get("order"), ["desc", "asc"] as const, "desc"),
});

/**
 * Filters → URL state. Defaults are left out so the bookmarkable URL stays
 * short; any unrelated params already on `base` are kept.
 */
export const filtersToParams = (
  filters: CaseManageFilters,
  base?: URLSearchParams,
): URLSearchParams => {
  const params = new URLSearchParams(base ? base.toString() : "");
  ["assigned", "inactiveDays", "never", "basis", "secondary", "q", "order"].forEach(
    (k) => params.delete(k),
  );
  const assigned = sanitizeAssigned(filters.assigned);
  if (assigned.length) params.set("assigned", assigned.join(","));
  if (filters.inactiveDays !== DEFAULT_INACTIVE_DAYS) {
    params.set("inactiveDays", String(filters.inactiveDays));
  }
  if (filters.never !== "include") params.set("never", filters.never);
  if (filters.basis !== "any") params.set("basis", filters.basis);
  if (filters.secondary !== "any") params.set("secondary", filters.secondary);
  if (filters.q.trim()) params.set("q", filters.q.trim());
  if (filters.order !== "desc") params.set("order", filters.order);
  return params;
};

/**
 * Query string for GET /cases/manage (with `paging`) or /cases/manage/ids
 * (without). The filter params are always sent explicitly so the request
 * never depends on server defaults; `assigned` / `q` only when non-empty.
 */
export const buildManageQuery = (
  filters: CaseManageFilters,
  paging?: { page: number; pageSize: number },
): string => {
  const params = new URLSearchParams();
  params.set(
    "inactiveDays",
    String(clampInactiveDays(filters.inactiveDays) ?? DEFAULT_INACTIVE_DAYS),
  );
  params.set("basis", filters.basis);
  params.set("never", filters.never);
  params.set("secondary", filters.secondary);
  params.set("order", filters.order);
  const assigned = sanitizeAssigned(filters.assigned);
  if (assigned.length) params.set("assigned", assigned.join(","));
  const q = filters.q.trim();
  if (q) params.set("q", q);
  if (paging) {
    params.set("page", String(Math.max(0, Math.floor(paging.page))));
    params.set("pageSize", String(Math.max(1, Math.floor(paging.pageSize))));
  }
  return params.toString();
};

export const chunk = <T>(items: T[], size: number): T[][] => {
  if (size < 1) throw new Error("chunk size must be >= 1");
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
};

// ---------------------------------------------------------------------------
// Users / the "Assigned to" picker
// ---------------------------------------------------------------------------

/** Same rule as the server's isUserActive(): deleted role or inactive status. */
export const isInactiveUser = (u: Partial<UserType> & { status?: string }) =>
  u.role === "deleted" || u.status === "inactive";

export const userLabel = (u: Partial<UserType>) =>
  `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "Unnamed user";

export interface AssignedOption {
  value: string;
  label: string;
  /** "shortcut" for the inactive/missing sentinels. */
  kind: "shortcut" | "active" | "inactive";
}

export interface AssignedOptionGroup {
  label: string;
  options: AssignedOption[];
}

export const ASSIGNED_SHORTCUTS: AssignedOption[] = [
  { value: ASSIGNED_INACTIVE, label: "Any inactive consultant", kind: "shortcut" },
  { value: ASSIGNED_MISSING, label: "No valid owner", kind: "shortcut" },
];

/** Shortcuts, then active consultants, then inactive/deleted ones — each A→Z. */
export const buildAssignedOptions = (users: any[]): AssignedOptionGroup[] => {
  const byLabel = (a: AssignedOption, b: AssignedOption) =>
    a.label.localeCompare(b.label);
  const active: AssignedOption[] = [];
  const inactive: AssignedOption[] = [];
  (users || []).forEach((u) => {
    if (!u || !u._id) return;
    const option: AssignedOption = {
      value: String(u._id),
      label: userLabel(u),
      kind: isInactiveUser(u) ? "inactive" : "active",
    };
    (option.kind === "inactive" ? inactive : active).push(option);
  });
  const groups: AssignedOptionGroup[] = [
    { label: "Shortcuts", options: ASSIGNED_SHORTCUTS },
  ];
  if (active.length) groups.push({ label: "Consultants", options: active.sort(byLabel) });
  if (inactive.length) {
    groups.push({ label: "Inactive consultants", options: inactive.sort(byLabel) });
  }
  return groups;
};

// ---------------------------------------------------------------------------
// Close reason prefill
// ---------------------------------------------------------------------------

const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * Reason prefilled into the close dialog, describing the filter that found
 * the cases, e.g. "No activity for 365+ days; assigned consultant inactive".
 * `nameFor` resolves a user id in `assigned` to a display name.
 */
export const buildCloseReason = (
  filters: CaseManageFilters,
  nameFor: (userId: string) => string | undefined = () => undefined,
): string => {
  const parts: string[] = [];
  const noun = filters.basis === "interaction" ? "interaction" : "activity";
  if (filters.never === "only") {
    parts.push(`no ${noun} ever recorded`);
  } else {
    parts.push(`no ${noun} for ${filters.inactiveDays}+ days`);
  }

  const assigned = sanitizeAssigned(filters.assigned);
  const wantsInactive = assigned.includes(ASSIGNED_INACTIVE);
  const wantsMissing = assigned.includes(ASSIGNED_MISSING);
  if (wantsInactive && wantsMissing) {
    parts.push("assigned consultant inactive or missing");
  } else if (wantsInactive) {
    parts.push("assigned consultant inactive");
  } else if (wantsMissing) {
    parts.push("no valid assigned consultant");
  }
  const names = assigned
    .filter((a) => a !== ASSIGNED_INACTIVE && a !== ASSIGNED_MISSING)
    .map((id) => nameFor(id) || "unknown user");
  if (names.length) {
    const shown = names.slice(0, 3).join(", ");
    const more = names.length > 3 ? ` +${names.length - 3} more` : "";
    parts.push(`assigned to ${shown}${more}`);
  }

  if (filters.secondary === "none_active") parts.push("no active secondary BFC");

  return capitalize(parts.join("; ")).slice(0, REASON_MAX);
};

export const isValidReason = (reason: string) => {
  const t = (reason || "").trim();
  return t.length >= REASON_MIN && t.length <= REASON_MAX;
};

// ---------------------------------------------------------------------------
// Selection maths
// ---------------------------------------------------------------------------

export interface WarningTotals {
  pendingReminders: number;
  openReferrals: number;
  openTasks: number;
  youngCases: number;
  /** Selected cases with at least one reminder/referral/task. */
  casesWithWarnings: number;
  /** How many of the asked-for ids had a loaded row to count from. */
  counted: number;
}

/** Warnings summed over the ids that have a loaded row. */
export const sumWarnings = (
  ids: string[],
  rowsById: Record<string, ManagedCaseRow | undefined>,
): WarningTotals => {
  const totals: WarningTotals = {
    pendingReminders: 0,
    openReferrals: 0,
    openTasks: 0,
    youngCases: 0,
    casesWithWarnings: 0,
    counted: 0,
  };
  ids.forEach((id) => {
    const row = rowsById[id];
    if (!row) return;
    const w = row.warnings || ({} as ManagedCaseRow["warnings"]);
    const reminders = Number(w.pendingReminders) || 0;
    const referrals = Number(w.openReferrals) || 0;
    const tasks = Number(w.openTasks) || 0;
    totals.counted++;
    totals.pendingReminders += reminders;
    totals.openReferrals += referrals;
    totals.openTasks += tasks;
    if (w.youngCase) totals.youngCases++;
    if (reminders + referrals + tasks > 0) totals.casesWithWarnings++;
  });
  return totals;
};

/**
 * The ids a bulk close should send: the selection minus cases KNOWN to be
 * young. Ids without a loaded row (from "select all matching") are kept —
 * the server skips young ones itself and reports them as `too_young`.
 */
export const closeEligibleIds = (
  ids: string[],
  rowsById: Record<string, ManagedCaseRow | undefined>,
): string[] => ids.filter((id) => !rowsById[id]?.warnings?.youngCase);

// ---------------------------------------------------------------------------
// Chunked writes
// ---------------------------------------------------------------------------

export interface BulkCloseBody {
  case_ids: string[];
  reason: string;
  cancelReminders: boolean;
  batch_id?: string;
}

export const emptyCloseResult = (): BulkCloseResult => ({
  batch_id: "",
  closed: 0,
  skipped: 0,
  skippedReasons: { not_found: 0, not_open: 0, too_young: 0 },
  remindersCancelled: 0,
});

export const mergeCloseResults = (
  a: BulkCloseResult,
  b: Partial<BulkCloseResult>,
): BulkCloseResult => ({
  batch_id: a.batch_id || b.batch_id || "",
  closed: a.closed + (Number(b.closed) || 0),
  skipped: a.skipped + (Number(b.skipped) || 0),
  skippedReasons: {
    not_found: a.skippedReasons.not_found + (Number(b.skippedReasons?.not_found) || 0),
    not_open: a.skippedReasons.not_open + (Number(b.skippedReasons?.not_open) || 0),
    too_young: a.skippedReasons.too_young + (Number(b.skippedReasons?.too_young) || 0),
  },
  remindersCancelled: a.remindersCancelled + (Number(b.remindersCancelled) || 0),
});

/** Thrown by the runners when a chunk fails; carries what already succeeded. */
export class BulkRunError<T> extends Error {
  partial: T;
  chunksDone: number;
  cause: unknown;
  constructor(message: string, partial: T, chunksDone: number, cause: unknown) {
    super(message);
    this.name = "BulkRunError";
    this.partial = partial;
    this.chunksDone = chunksDone;
    this.cause = cause;
  }
}

/**
 * POST /cases/bulk-close in 500-id chunks. The first response's `batch_id`
 * is sent back on every later chunk so one logical batch (one Undo) spans
 * all the calls. Stops at the first failed chunk and throws a BulkRunError
 * carrying the merged result so far.
 */
export const runBulkClose = async (
  ids: string[],
  opts: { reason: string; cancelReminders: boolean },
  post: (body: BulkCloseBody) => Promise<BulkCloseResult>,
  onProgress?: (done: number, total: number) => void,
): Promise<BulkCloseResult> => {
  const chunks = chunk(ids, BULK_CHUNK_SIZE);
  let result = emptyCloseResult();
  for (let i = 0; i < chunks.length; i++) {
    const body: BulkCloseBody = {
      case_ids: chunks[i],
      reason: opts.reason.trim(),
      cancelReminders: opts.cancelReminders,
    };
    if (result.batch_id) body.batch_id = result.batch_id;
    try {
      const res = await post(body);
      result = mergeCloseResults(result, res || {});
    } catch (err) {
      throw new BulkRunError("bulk close failed", result, i, err);
    }
    onProgress?.(i + 1, chunks.length);
  }
  return result;
};

/** POST /cases/bulk-reassign in 500-id chunks, totals merged. */
export const runBulkReassign = async (
  ids: string[],
  successorUserId: string,
  post: (body: { case_ids: string[]; successorUserId: string }) => Promise<BulkReassignResult>,
  onProgress?: (done: number, total: number) => void,
): Promise<BulkReassignResult> => {
  const chunks = chunk(ids, BULK_CHUNK_SIZE);
  const result: BulkReassignResult = { reassigned: 0, skipped: 0 };
  for (let i = 0; i < chunks.length; i++) {
    try {
      const res = await post({ case_ids: chunks[i], successorUserId });
      result.reassigned += Number(res?.reassigned) || 0;
      result.skipped += Number(res?.skipped) || 0;
    } catch (err) {
      throw new BulkRunError("bulk reassign failed", { ...result }, i, err);
    }
    onProgress?.(i + 1, chunks.length);
  }
  return result;
};

/**
 * Every row of a filter result, paging GET /cases/manage EXPORT_PAGE_SIZE at
 * a time (capped at EXPORT_ROW_CAP, like /ids). `get` receives the query
 * string. Each page carries its own warnings, so the rows are complete.
 */
export const fetchAllFilterRows = async (
  filters: CaseManageFilters,
  get: (query: string) => Promise<ManagedCasesResponse>,
  onProgress?: (loaded: number, total: number) => void,
): Promise<{ rows: ManagedCaseRow[]; total: number; truncated: boolean }> => {
  const rows: ManagedCaseRow[] = [];
  const seen = new Set<string>();
  let total = 0;
  for (let page = 0; rows.length < EXPORT_ROW_CAP; page++) {
    const res = await get(buildManageQuery(filters, { page, pageSize: EXPORT_PAGE_SIZE }));
    const pageRows = Array.isArray(res?.rows) ? res.rows : [];
    total = typeof res?.total === "number" ? res.total : total;
    pageRows.forEach((r) => {
      if (!seen.has(r.case_id) && rows.length < EXPORT_ROW_CAP) {
        seen.add(r.case_id);
        rows.push(r);
      }
    });
    onProgress?.(rows.length, Math.min(total, EXPORT_ROW_CAP));
    if (pageRows.length < EXPORT_PAGE_SIZE || rows.length >= total) break;
  }
  return { rows, total, truncated: total > rows.length };
};

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------

/**
 * One CSV cell. Quotes when needed (comma, quote, CR/LF) and neutralises
 * spreadsheet formula injection: a TEXT value starting with = + - @ or a
 * tab/CR gets a leading apostrophe. Numbers are written as-is.
 */
export const csvCell = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  let text = String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const pad2 = (n: number) => String(n).padStart(2, "0");

/** ISO timestamp → local YYYY-MM-DD ("" for null / unparseable). */
export const csvDate = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

export const CSV_COLUMNS = [
  "Case ID",
  "Firm",
  "Firm ID",
  "Assigned to",
  "Assigned status",
  "Secondary BFC",
  "Secondary status",
  "Last activity",
  "Days inactive",
  "Opened",
  "Pending reminders",
  "Open referrals",
  "Open tasks",
  `Opened <${YOUNG_CASE_DAYS} days`,
  "Case link",
];

/** `origin` (e.g. window.location.origin) makes the Case link absolute. */
export const buildCasesCsv = (rows: ManagedCaseRow[], origin = ""): string => {
  const lines = [CSV_COLUMNS.map(csvCell).join(",")];
  rows.forEach((r) => {
    const w = r.warnings || ({} as ManagedCaseRow["warnings"]);
    lines.push(
      [
        r.case_id,
        r.firm_name,
        r.firm_id,
        r.owner_name,
        r.owner_status,
        r.secondary_name,
        r.secondary_status,
        csvDate(r.lastActivity),
        r.daysInactive === null || r.daysInactive === undefined ? "Never" : r.daysInactive,
        csvDate(r.caseOpened),
        Number(w.pendingReminders) || 0,
        Number(w.openReferrals) || 0,
        Number(w.openTasks) || 0,
        !!w.youngCase,
        `${origin}/case?id=${r.case_id}`,
      ]
        .map(csvCell)
        .join(","),
    );
  });
  return lines.join("\r\n");
};

export const casesCsvFilename = (label: string, date = new Date()) =>
  `case-management_${label}_${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
    date.getDate(),
  )}.csv`;

// ---------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------

export const plural = (n: number, one: string, many = `${one}s`) =>
  `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** "2 not open, 1 opened <30 days ago" — empty string when nothing skipped. */
export const describeSkipped = (reasons: BulkCloseResult["skippedReasons"]) => {
  const parts: string[] = [];
  if (reasons?.not_open) parts.push(`${reasons.not_open.toLocaleString()} not open`);
  if (reasons?.too_young) {
    parts.push(`${reasons.too_young.toLocaleString()} opened <${YOUNG_CASE_DAYS} days ago`);
  }
  if (reasons?.not_found) parts.push(`${reasons.not_found.toLocaleString()} not found`);
  return parts.join(", ");
};

/** Human summary of the filter for the page subtitle. */
export const describeFilters = (filters: CaseManageFilters): string => {
  const noun = filters.basis === "interaction" ? "interaction" : "activity";
  const age =
    filters.never === "only"
      ? `with no ${noun} ever recorded`
      : `with no ${noun} in the last ${filters.inactiveDays} day${
          filters.inactiveDays === 1 ? "" : "s"
        }${filters.never === "exclude" ? " (never-touched cases hidden)" : ""}`;
  return `Open cases ${age}, ${filters.order === "asc" ? "least" : "most"} stale first.`;
};
